export function calculateMasonryRowSpan(itemHeight: number, rowHeight: number): number {
  if (!Number.isFinite(itemHeight) || !Number.isFinite(rowHeight) || rowHeight <= 0) return 1
  return Math.max(1, Math.ceil(itemHeight / rowHeight))
}

/**
 * Packs natural-height cards into fine CSS Grid rows. The DOM stays in source
 * order, Grid places tied items from inline-start to inline-end, and no card
 * measurement enters React state.
 */
export function installMeasuredMasonryLayout(
  container: HTMLElement,
  { stableColumns = false }: { stableColumns?: boolean } = {},
): () => void {
  if (typeof ResizeObserver === 'undefined') return () => undefined

  let animationFrame = 0
  const observedItems = new Set<HTMLElement>()
  const items = () => [...container.children].filter(
    (child): child is HTMLElement => child instanceof HTMLElement,
  )

  const layout = () => {
    animationFrame = 0
    const containerStyle = getComputedStyle(container)
    const rowHeight = Number.parseFloat(containerStyle.gridAutoRows)
    if (!Number.isFinite(rowHeight) || rowHeight <= 0) return

    // Stable-column CSS marks the end of its explicit tracks. Computed Grid styles
    // also contain implicit tracks created by our OLD placements after a shrink:
    // those must not become available columns. Conversely, an explicit 0px auto-fit
    // track is available after growth. Missing marker safely falls back to one column.
    const template = containerStyle.gridTemplateColumns ?? ''
    const boundary = template.search(/\[[^\]]*\bmasonry-end\b[^\]]*\]/)
    const columns = stableColumns && boundary >= 0
      ? Math.max(1, template.slice(0, boundary).replace(/\[[^\]]*\]/g, '').trim().split(/\s+/)
        .filter(track => Number.isFinite(Number.parseFloat(track))).length)
      : 1
    const measurements = items().map((item) => {
      const style = stableColumns ? getComputedStyle(item) : null
      return {
        item,
        order: Number(style?.order) || 0,
        fullWidth: style?.gridColumnEnd === '-1',
      }
    })
    // Optional disclosure layout: CSS still owns column count/width and spacing.
    // Fixed source-order columns prevent a neighbor's height from moving a card
    // across the board. Full-width shared material forms a boundary for all columns.
    const gapRows = stableColumns ? Math.ceil((Number.parseFloat(containerStyle.columnGap) || 0) / rowHeight) : 0
    const nextRows = Array<number>(columns).fill(1)
    let column = 0
    if (stableColumns) {
      measurements.sort((a, b) => a.order - b.order)
      // Batch width-changing placement writes BEFORE reading heights. Otherwise a
      // formerly squeezed card leaves a huge stale row span for another paint.
      for (const { item, fullWidth } of measurements) {
        const nextColumn = String(fullWidth ? 1 : column + 1)
        if (item.style.gridColumnStart !== nextColumn) item.style.gridColumnStart = nextColumn
        column = fullWidth ? 0 : (column + 1) % columns
      }
    }
    const spans = measurements.map(({ item }) => calculateMasonryRowSpan(item.getBoundingClientRect().height, rowHeight))
    column = 0
    for (const [index, { item, fullWidth }] of measurements.entries()) {
      const span = spans[index]!
      if (stableColumns) {
        const start = fullWidth ? Math.max(...nextRows) : nextRows[column]!
        if (item.style.gridRowStart !== String(start)) item.style.gridRowStart = String(start)
        if (fullWidth) { nextRows.fill(start + span + gapRows); column = 0 }
        else { nextRows[column] = start + span + gapRows; column = (column + 1) % columns }
      }
      const next = `span ${span}`
      if (item.style.gridRowEnd !== next) item.style.gridRowEnd = next
    }
  }

  const scheduleLayout = () => {
    if (animationFrame !== 0) return
    animationFrame = requestAnimationFrame(layout)
  }

  container.dataset.masonryLayout = 'measured'
  layout()

  const observer = new ResizeObserver(scheduleLayout)
  observer.observe(container)
  const observeItem = (item: HTMLElement) => {
    if (observedItems.has(item)) return
    observedItems.add(item)
    observer.observe(item)
  }
  const unobserveItem = (item: HTMLElement) => {
    if (!observedItems.delete(item)) return
    observer.unobserve(item)
  }
  for (const item of items()) observeItem(item)

  const mutationObserver = typeof MutationObserver === 'undefined'
    ? null
    : new MutationObserver((records) => {
      for (const record of records) {
        for (const node of record.removedNodes) {
          if (node instanceof HTMLElement) unobserveItem(node)
        }
        for (const node of record.addedNodes) {
          if (node instanceof HTMLElement) observeItem(node)
        }
      }
      scheduleLayout()
    })
  mutationObserver?.observe(container, { childList: true })

  return () => {
    mutationObserver?.disconnect()
    observer.disconnect()
    observedItems.clear()
    if (animationFrame !== 0) cancelAnimationFrame(animationFrame)
    delete container.dataset.masonryLayout
    for (const item of items()) {
      item.style.gridRowEnd = ''
      if (stableColumns) { item.style.gridRowStart = ''; item.style.gridColumnStart = '' }
    }
  }
}
